'use client';

import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'next-i18next';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
    Box,
    Flex,
    GridItem,
    Heading,
    HStack,
    Text,
    useSlotRecipe,
    VStack,
} from '@chakra-ui/react';

import isNil from 'lodash/isNil';

import { TileListing } from '@nocowanie/noc-api';

import { GridWrapper } from '@nocowanie/common-ui/components/atoms/grid-wrapper';
import { SeoLinks } from '@nocowanie/common-ui/components/molecules/seo-links';
import { usePluralisationTranslation } from '@nocowanie/common-ui/hooks';
import { IconsLinear } from '@nocowanie/icons/linear';

import {
    commonUiStateSlice,
    listingStateSlice,
    searchStateSlice,
    setSearchLoadingAction,
    useAppDispatch,
    useAppSelector,
} from '@app/app-state';
import { AnimatedCollapse } from '@app/components/atoms/animated-collapse';
import { IndicatorButtonSkeleton } from '@app/components/atoms/indicator-button/indicator-button-skeleton';
import { ListingBreadcrumbs } from '@app/components/atoms/listing-breadcrumbs';
import { ListingDescription } from '@app/components/atoms/listing-description';
import { MessageBox } from '@app/components/atoms/message-box';
import { ResultsOrderInfo } from '@app/components/atoms/results-order-info';
import { AccommodationTile } from '@app/components/molecules/accommodation-tile';
import { ErrorInfo } from '@app/components/molecules/error-info';
import { ListingActiveFilters } from '@app/components/molecules/listing-active-filters';
import { ListingFaq } from '@app/components/molecules/listing-faq';
import { ListingSearchBarSkeleton } from '@app/components/molecules/listing-search-bar/listing-search-bar-skeleton';
import { MapEntryPointSkeleton } from '@app/components/molecules/map-entry-point/map-entry-point-skeleton';
import { NearbyAccommodationsMessage } from '@app/components/molecules/nearby-accommodations-message';
import { Pagination } from '@app/components/molecules/pagination';
import { SortingSelectSkeleton } from '@app/components/molecules/sorting-select/sorting-select-skeleton';
import { StickySpinner } from '@app/components/molecules/sticky-spinner';
import { SwitchStickyBarSkeleton } from '@app/components/molecules/switch-sticky-bar/switch-sticky-bar-skeleton';
import { ListingFiltersSkeleton } from '@app/components/organisms/listing-filters/listing-filters-skeleton';
import { Support } from '@app/components/organisms/support';
import { ListingLayoutProps } from '@app/components/pages/listing';
import { SEARCH_CONSTS } from '@app/consts/consts';
import { ACCOMMODATION_TILE } from '@app/consts/listing.consts';
import { LocationTypeEnum } from '@app/enums/location-type.enum';
import { resolveListingHeader } from '@app/helpers/listing.helpers';
import { useNocSmListingSearchPageVisit } from '@app/hooks/sales-manago';
import { useActiveFilters } from '@app/hooks/useActiveFilters';
import { useDataLayerListingEvents } from '@app/hooks/useDataLayerListingEvents';
import { useListingSearch } from '@app/hooks/useListingSearch';
import { useSearch } from '@app/hooks/useSearch';
import { useSearchbarScrollBehavior } from '@app/hooks/useSearchbarScrollBehavior';
import { useHasCookieSearchData, useSearchInitializer } from '@app/hooks/useSearchInitializer';
import { GeoCityModel } from '@app/models/geo.model';
import { setMetaHeader } from '@app/state/slices/listing-state.slice';

const MapButton = dynamic(
    () => import('@app/components/molecules/map-button').then(mod => mod.MapButton),
    {
        ssr: false,
        loading: () => <IndicatorButtonSkeleton />,
    },
);

const MapEntryPoint = dynamic(
    () => import('@app/components/molecules/map-entry-point').then(mod => mod.MapEntryPoint),
    {
        ssr: false,
        loading: () => <MapEntryPointSkeleton />,
    },
);

const SortingDrawer = dynamic(
    () => import('@app/components/molecules/sorting-drawer').then(mod => mod.SortingDrawer),
    {
        ssr: false,
        loading: () => <IndicatorButtonSkeleton />,
    },
);

const SortingSelect = dynamic(
    () => import('@app/components/molecules/sorting-select').then(mod => mod.SortingSelect),
    {
        ssr: false,
        loading: () => <SortingSelectSkeleton />,
    },
);

const ListingFilters = dynamic(
    () => import('@app/components/organisms/listing-filters').then(mod => mod.ListingFilters),
    {
        ssr: false,
        loading: () => <ListingFiltersSkeleton />,
    },
);

const SwitchStickyBar = dynamic(
    () => import('@app/components/molecules/switch-sticky-bar').then(mod => mod.SwitchStickyBar),
    {
        ssr: false,
        loading: () => <SwitchStickyBarSkeleton />,
    },
);
const ListingSearchBar = dynamic(
    () => import('@app/components/molecules/listing-search-bar').then(mod => mod.ListingSearchBar),
    {
        ssr: false,
        loading: () => <ListingSearchBarSkeleton />,
    },
);

export const ListingLayout = ({
    preloadedMeta,
    preloadedItems,
    initialLocationData,
    withMap = true,
    breadcrumbs,
    breadcrumbName,
    linkspace,
    cmsDescription,
    cmsFaq,
    filtersOverride,
}: ListingLayoutProps) => {
    const isStatePreloaded = useRef(false);
    const skipFirstFetch = useRef(true);
    const hasPreloadedItems = useRef(preloadedItems?.data);
    const isInitialMount = useRef(true);
    const criteriaChangedRef = useRef(true);
    const willFetchRef = useRef(false);
    const dispatch = useAppDispatch();
    const { t } = useTranslation('listing');
    const { t: tileTranslations } = useTranslation('common', {
        keyPrefix: 'accommodationTile',
    });
    const { t: tMessage } = useTranslation('common', {
        keyPrefix: 'nearbyAccommodationsMessage',
    });
    const pluralizeTranslation = usePluralisationTranslation('listing');
    const pageItemCount = SEARCH_CONSTS.PAGE_ITEM_COUNT;

    const searchParams = useSearchParams();
    const { isInitialRender, isMobileRes, toggleFiltersVisibility } = useSearch();
    const initializeSearch = useSearchInitializer({
        autoOpenFiltersOnDesktop: false,
    });
    const hasCookieSearchData = useHasCookieSearchData();
    const {
        campaignListingId,
        fetchListingItems,
        searchCriteria,
        filters,
        currentPage,
        isSearchLoading,
    } = useListingSearch();

    const { activeFilters } = useActiveFilters();
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const listingMeta = useAppSelector(listingStateSlice.selectors.selectListingMeta);
    const listingMetaData = useMemo(
        () =>
            listingMeta.data.header ? listingMeta.data : (preloadedMeta?.data ?? listingMeta.data),
        [listingMeta.data, preloadedMeta?.data],
    );

    const {
        header,
        itemCount,
        cmsId,
        checkIn: cmsCheckIn,
        checkOut: cmsCheckOut,
        flexDates: cmsFlexDates,
        locationDetails,
    } = listingMetaData;

    const isListingItemsError = useAppSelector(
        listingStateSlice.selectors.selectIsListingItemsError,
    );
    const stateItems = useAppSelector(listingStateSlice.selectors.selectListingItems);

    useNocSmListingSearchPageVisit({
        activeFilters,
        searchCriteria,
    });

    const allItems = useMemo(() => {
        return stateItems ?? preloadedItems?.data?.tiles;
    }, [stateItems, preloadedItems?.data?.tiles]);

    const lastSearchTimestamp = useAppSelector(
        searchStateSlice.selectors.selectSearchLastTimestamp,
    );
    const recipe = useSlotRecipe({ key: 'listingLayout' });
    const styles = recipe({});

    const tileRecipe = useSlotRecipe({ key: 'accommodationTile' });
    const tileStyles = useMemo(() => tileRecipe({}), [tileRecipe]);
    const [shouldFetchItems, setShouldFetchItems] = useState(false);
    const {
        anchorRef: searchbarAnchorRef,
        searchbarRef,
        isScrolledPast: isSearchbarSticky,
        searchbarTransform,
    } = useSearchbarScrollBehavior();
    const { sendSearchResultsDataLayerEvent } = useDataLayerListingEvents();

    useEffect(() => {
        if (!isStatePreloaded.current && hasPreloadedItems.current && preloadedMeta?.data) {
            dispatch(listingStateSlice.setMeta(preloadedMeta));
            dispatch(listingStateSlice.setItems(preloadedItems));
            isStatePreloaded.current = true;
        } else if (!isStatePreloaded.current) {
            console.warn('[ListingLayout] NOT dispatching - validation failed:', {
                hasPreloadedItems: !!preloadedItems?.data?.tiles,
                hasPreloadedMeta: !!preloadedMeta?.data,
            });
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(searchParams?.toString());

        if (initialLocationData) {
            if (initialLocationData.geoId && !params.has('geoId')) {
                params.set('geoId', initialLocationData.geoId);
            }
            if (initialLocationData.locationType && !params.has('type')) {
                params.set('type', initialLocationData.locationType);
            }
            if (initialLocationData.location && !params.has('location')) {
                params.set('location', initialLocationData.location);
            }
        }

        if (hasCookieSearchData() && !params.has('checkInDate')) {
            skipFirstFetch.current = false;
        }

        initializeSearch(
            params,
            campaignListingId,
            undefined,
            {
                checkIn: cmsCheckIn,
                checkOut: cmsCheckOut,
                flexDates: cmsFlexDates,
            },
            filtersOverride,
        );
    }, []);

    useEffect(() => {
        willFetchRef.current = !skipFirstFetch.current;

        // Skip only the initial fetch if we have preloaded items from server
        if (isInitialMount.current && hasPreloadedItems.current) {
            isInitialMount.current = false;
            return;
        }

        isInitialMount.current = false;
        setShouldFetchItems(true);
    }, [campaignListingId, currentPage, filters, searchCriteria]);

    useEffect(() => {
        if (skipFirstFetch.current || !shouldFetchItems || campaignListingId) {
            return;
        }

        dispatch(
            setMetaHeader(
                resolveListingHeader(t, {
                    locationName: searchCriteria?.location,
                }),
            ),
        );
    }, [filters, searchCriteria?.location, shouldFetchItems, campaignListingId]);

    useEffect(() => {
        if (!shouldFetchItems) {
            return;
        }

        if (skipFirstFetch.current) {
            skipFirstFetch.current = false;
            return;
        }

        dispatch(setSearchLoadingAction(true));
        setShouldFetchItems(false);
        fetchListingItems();
    }, [shouldFetchItems, fetchListingItems]);

    useEffect(() => {
        dispatch(setSearchLoadingAction(false));
        willFetchRef.current = false;
    }, [allItems]);

    useEffect(() => {
        criteriaChangedRef.current = true;
    }, [searchCriteria]);

    useEffect(() => {
        if (
            allItems &&
            lastSearchTimestamp &&
            criteriaChangedRef.current &&
            !willFetchRef.current
        ) {
            criteriaChangedRef.current = false;
            sendSearchResultsDataLayerEvent();
        }
    }, [allItems, sendSearchResultsDataLayerEvent, lastSearchTimestamp]);

    useEffect(() => {
        if (!isListingItemsError) {
            return;
        }

        dispatch(setSearchLoadingAction(false));
    }, [isListingItemsError]);

    const startPosition = ((currentPage || 1) - 1) * pageItemCount + 1;

    // Detect first Nearby placement
    const placementChangeIndex = useMemo(
        () =>
            allItems?.findIndex(tile => tile?.listing?.placement === TileListing.placement.NEARBY),
        [allItems],
    );

    const renderBreadcrumbs = () => {
        if (!cmsId && breadcrumbs && breadcrumbName) {
            return <ListingBreadcrumbs breadcrumbs={breadcrumbs} name={breadcrumbName} />;
        }
    };

    const cityCenterLocation = useMemo(
        () =>
            !isNil(locationDetails)
                ? { lat: locationDetails.location.lat, lon: locationDetails.location.lon }
                : undefined,
        [locationDetails],
    );
    const showDistanceFromCenterOption = initialLocationData?.locationType
        ? (
              [
                  LocationTypeEnum.City,
                  LocationTypeEnum.Region,
                  LocationTypeEnum.Voivodeship,
              ] as string[]
          ).includes(initialLocationData.locationType)
        : false;
    const cityNameVarieties = useMemo(
        () =>
            !isNil(locationDetails)
                ? {
                      genitive: (locationDetails as GeoCityModel).varietyGenitive,
                      locative: locationDetails.varietyLocative,
                  }
                : undefined,
        [locationDetails],
    );

    const renderedTiles = useMemo(() => {
        if (isListingItemsError || !allItems?.length) {
            return null;
        }

        const hasDates = !!searchCriteria.checkInDate && !!searchCriteria.checkOutDate;

        return allItems.map((tileData, index) => (
            <React.Fragment key={tileData.id || index}>
                {placementChangeIndex === index && (
                    <NearbyAccommodationsMessage
                        locationName={searchCriteria?.location}
                        withDates={hasDates}
                        t={tMessage}
                    />
                )}
                <AccommodationTile
                    position={startPosition + index}
                    searchCriteria={searchCriteria}
                    searchFilters={filters}
                    initialData={tileData}
                    amenitiesMaxVisible={isMobileRes ? ACCOMMODATION_TILE.MAX_AMENITIES : undefined}
                    priorityCount={3}
                    cityLocation={cityCenterLocation}
                    locationVarieties={cityNameVarieties}
                    styles={tileStyles}
                    t={tileTranslations}
                    isMobileBrowser={isMobileBrowser}
                />
            </React.Fragment>
        ));
    }, [
        allItems,
        filters,
        isListingItemsError,
        isMobileRes,
        placementChangeIndex,
        searchCriteria,
        startPosition,
        tMessage,
        tileTranslations,
        tileStyles,
        cityCenterLocation,
        cityNameVarieties,
        isMobileBrowser,
    ]);

    return (
        <>
            <main>
                {isMobileRes && renderBreadcrumbs()}
                <Box ref={searchbarAnchorRef} />
                <Box
                    css={styles.searchbar}
                    boxShadow={isSearchbarSticky ? 'stickyHeader' : 'none'}
                    transform={isMobileRes ? searchbarTransform : undefined}
                    bg={isMobileRes || isSearchbarSticky ? 'white' : 'transparent'}
                >
                    <Box ref={searchbarRef}>
                        <ListingSearchBar
                            cmsCheckIn={cmsCheckIn}
                            cmsCheckOut={cmsCheckOut}
                            cmsFlexDates={cmsFlexDates}
                            locationPlaceholder={campaignListingId && header ? header : undefined}
                            mb={{ base: 2, md: 0 }}
                        />
                    </Box>
                    {!isListingItemsError ? (
                        <Box
                            css={styles.actionBar}
                            borderBottomColor={!isSearchbarSticky ? 'border' : 'transparent'}
                        >
                            {withMap ? <MapButton /> : null}
                            <ListingFilters variant="mobile" />
                            <SortingDrawer
                                showDistanceFromCenterOption={showDistanceFromCenterOption}
                            />
                        </Box>
                    ) : null}
                </Box>
                {!isMobileRes && renderBreadcrumbs()}
                <GridWrapper
                    gridTemplateColumns={{
                        base: '1fr',
                        lg2: '320px auto',
                    }}
                    columnGap={{ base: 0, lg2: 8 }}
                    rowGap={0}
                >
                    <GridItem css={styles.sidebar}>
                        {withMap ? <MapEntryPoint /> : null}
                        <ListingFilters variant="desktop" />
                    </GridItem>
                    <GridItem as={VStack} alignItems={'stretch'} css={styles.searchResultsWrapper}>
                        {!isListingItemsError ? (
                            <>
                                <HStack css={styles.headingWrapper} alignItems={'start'}>
                                    <Flex css={styles.pageHeading}>
                                        <Heading as={'h1'}>{header}</Heading>
                                        <Text as={'span'} color={'gray.600'}>
                                            {pluralizeTranslation(itemCount, 'accommodation')}
                                        </Text>
                                    </Flex>
                                    <SortingSelect
                                        showDistanceFromCenterOption={showDistanceFromCenterOption}
                                    />
                                </HStack>

                                {!isMobileRes ? (
                                    <AnimatedCollapse
                                        isOpen={activeFilters.length > 0 && !isInitialRender}
                                        css={styles.activeFilters}
                                        mb={activeFilters.length > 0 ? 2 : 0}
                                    >
                                        <ListingActiveFilters />
                                    </AnimatedCollapse>
                                ) : undefined}

                                {!isListingItemsError ? <ResultsOrderInfo mb={2} /> : null}
                            </>
                        ) : null}
                        <Box as={'section'}>
                            {isListingItemsError ? (
                                <ErrorInfo data-testid={'error-info'} mt={4} mb={8} />
                            ) : !allItems?.length ? (
                                <MessageBox
                                    title={t('noResults.title')}
                                    message={t('noResults.message')}
                                    mb={4}
                                    boxShadow={'tile'}
                                    data-testid={'no-results-message'}
                                />
                            ) : (
                                renderedTiles
                            )}
                        </Box>
                        {!isListingItemsError ? (
                            <>
                                <Pagination initialPageCount={listingMetaData.pageCount} />
                                <HStack
                                    css={styles.filtersInfoBox}
                                    gap={4}
                                    onClick={() => isMobileRes && toggleFiltersVisibility()}
                                >
                                    <HStack as={'span'}>
                                        <IconsLinear.Settings />
                                    </HStack>
                                    <Text
                                        flex={1}
                                        dangerouslySetInnerHTML={{ __html: t('filtersInfo') }}
                                    />
                                </HStack>
                            </>
                        ) : null}
                        <StickySpinner isLoading={isSearchLoading} left={-4} right={-4} />
                        {!searchParams?.size && (
                            <>
                                {cmsDescription ? (
                                    <ListingDescription description={cmsDescription} />
                                ) : null}
                                {cmsFaq ? <ListingFaq faqData={cmsFaq} /> : null}
                            </>
                        )}
                        {linkspace ? <SeoLinks linkSections={linkspace} mb={8} /> : null}
                    </GridItem>
                </GridWrapper>
                {isMobileRes && !isListingItemsError ? <SwitchStickyBar /> : null}
            </main>
            <Support />
        </>
    );
};
